import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

const AnalyticsComponent = ({ user }) => {
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [velocity, setVelocity] = useState(null);
  const [modules, setModules] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(7);

  useEffect(() => { fetchAll(); }, [timeRange]);

  const fetchAll = async () => {
    try {
      const [ov, tl, vel, mod, jur] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/analytics/activity-timeline?days=${timeRange}`),
        axios.get(`${API}/analytics/economic-velocity`),
        axios.get(`${API}/analytics/top-modules`),
        axios.get(`${API}/analytics/jurisdiction-stats`)
      ]);
      setOverview(ov.data);
      setTimeline(tl.data.timeline || []);
      setVelocity(vel.data);
      setModules(mod.data.modules || []);
      setJurisdictions(jur.data.jurisdictions || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando analytics...</div>;

  return (
    <div className="space-y-6" data-testid="analytics-module">
      {/* Overview cards */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Usuarios', value: overview.users, color: 'indigo' },
            { label: 'Transacciones', value: overview.transactions, color: 'green' },
            { label: 'Volumen Total', value: `${overview.transaction_volume} Cc`, color: 'amber' },
            { label: 'Viajes', value: overview.rides, color: 'cyan' },
            { label: 'Escrows', value: overview.escrows, color: 'purple' },
            { label: 'Bloques', value: overview.blocks, color: 'gray' },
            { label: 'Automatizaciones', value: overview.active_automations, color: 'pink' },
            { label: 'Vendedores', value: overview.seller_accounts, color: 'teal' },
          ].map((m, i) => (
            <div key={i} className={`bg-white rounded-xl border border-gray-200 p-4 text-center`}>
              <p className={`text-2xl font-bold text-${m.color}-600`}>{m.value}</p>
              <p className="text-xs text-gray-500 mt-1">{m.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Velocity */}
      {velocity && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-5" data-testid="velocity-card">
          <h3 className="text-sm font-semibold text-indigo-800 mb-3">Velocidad Economica</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-indigo-700">{velocity.velocity_txs_per_hour}</p>
              <p className="text-xs text-indigo-500">TX/hora (24h)</p>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-indigo-700">{velocity.weekly_volume}</p>
              <p className="text-xs text-indigo-500">Vol. Semanal (Cc)</p>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-amber-600">{velocity.locked_in_escrow}</p>
              <p className="text-xs text-amber-500">En Escrow (Cc)</p>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-green-600">{velocity.avg_tx_size}</p>
              <p className="text-xs text-green-500">TX Promedio (Cc)</p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="timeline-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Actividad por Dia</h3>
          <div className="flex gap-1">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setTimeRange(d)} data-testid={`range-${d}`}
                className={`px-3 py-1 rounded-lg text-xs font-medium ${timeRange === d ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={timeline}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="transactions" name="Transacciones" fill="#6366f1" radius={[4, 4, 0, 0]} />
            <Bar dataKey="rides" name="Viajes" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="escrows" name="Escrows" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Bar dataKey="new_users" name="Nuevos Usuarios" fill="#ec4899" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom row: Modules + Jurisdictions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top Modules */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="modules-chart">
          <h3 className="font-semibold text-gray-900 mb-4">Modulos Mas Usados</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={modules.filter(m => m.count > 0)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]}>
                {modules.filter(m => m.count > 0).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Jurisdictions */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="jurisdictions-chart">
          <h3 className="font-semibold text-gray-900 mb-4">Usuarios por Jurisdiccion</h3>
          {jurisdictions.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={jurisdictions} dataKey="users" nameKey="country" cx="50%" cy="50%"
                  outerRadius={80} label={({ country, users }) => `${country}: ${users}`}>
                  {jurisdictions.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">
              Sin datos de jurisdiccion aun
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsComponent;
